import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  PhoneIcon,
  EnvelopeIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { useCustomerList } from '../hooks/useCustomerList';
import { CustomerListItem } from '../services/customerService';
import CustomerDetailModal from './CustomerDetailModal';
import NewConversationModal from '../../conversations/components/NewConversationModal';
import { CallCustomerButton } from '../../voice';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores';

export default function CustomerDirectory() {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messageTarget, setMessageTarget] = useState<CustomerListItem | null>(null);
  const { data: customers = [], isLoading } = useCustomerList();
  const currency = useAuthStore((s) => s.currency);
  const navigate = useNavigate();

  // Client-side search filtering
  const filtered = useMemo(() => {
    if (!search.trim()) return customers;
    const q = search.toLowerCase();
    return customers.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.customerName || '').toLowerCase().includes(q) ||
        (c.phoneNumber || '').includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        c.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [customers, search]);

  const displayName = (c: CustomerListItem) =>
    c.name || c.customerName || c.phoneNumber;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Customers</h1>
          <p className="text-sm text-ink-3">
            {customers.length} customer{customers.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-3" />
        <input
          type="text"
          placeholder="Search by name, phone, email, or tag..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-xl border border-line bg-surface py-2.5 pl-10 pr-4 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center rounded-card border border-dashed border-line text-ink-3">
          <p className="font-medium">No customers found</p>
          <p className="mt-1 text-sm">
            {search ? 'Try a different search term' : 'Customers will appear here once they message you'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-line shadow-s">
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-2">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-3">
                  Customer
                </th>
                <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-3 md:table-cell">
                  Contact
                </th>
                <th className="hidden px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-ink-3 lg:table-cell">
                  Orders
                </th>
                <th className="hidden px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-ink-3 lg:table-cell">
                  Value
                </th>
                <th className="hidden px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-ink-3 sm:table-cell">
                  Conversations
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-3">
                  Last Contact
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-ink-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-surface">
              {filtered.map((customer) => (
                <tr
                  key={customer.id}
                  onClick={() => setSelectedId(customer.id)}
                  className="cursor-pointer transition-colors hover:bg-black/[0.03]"
                >
                  {/* Name + Tags */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/10 text-sm font-semibold text-brand">
                        {displayName(customer).charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">
                          {displayName(customer)}
                        </p>
                        {customer.tags.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-1">
                            {customer.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex rounded-full bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-ink-2"
                              >
                                {tag}
                              </span>
                            ))}
                            {customer.tags.length > 3 && (
                              <span className="text-[10px] text-ink-2">
                                +{customer.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Contact */}
                  <td className="hidden px-4 py-3 md:table-cell">
                    <div className="space-y-1 text-sm text-ink-2">
                      <div className="flex items-center gap-1.5">
                        <PhoneIcon className="h-3.5 w-3.5" />
                        <span className="truncate">{customer.phoneNumber}</span>
                      </div>
                      {customer.email && (
                        <div className="flex items-center gap-1.5">
                          <EnvelopeIcon className="h-3.5 w-3.5" />
                          <span className="truncate">{customer.email}</span>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Orders */}
                  <td className="hidden px-4 py-3 text-center lg:table-cell">
                    <div className="flex items-center justify-center gap-1 text-sm text-ink-2">
                      <ShoppingBagIcon className="h-4 w-4" />
                      {customer.totalOrders}
                    </div>
                  </td>

                  {/* Lifetime Value */}
                  <td className="hidden px-4 py-3 text-center lg:table-cell">
                    <div className="flex items-center justify-center gap-1 text-sm text-ink-2">
                      <CurrencyDollarIcon className="h-4 w-4" />
                      {formatCurrency(customer.lifetimeValue, currency)}
                    </div>
                  </td>

                  {/* Conversations */}
                  <td className="hidden px-4 py-3 text-center sm:table-cell">
                    <div className="flex items-center justify-center gap-1 text-sm text-ink-2">
                      <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      {customer.totalConversations}
                    </div>
                  </td>

                  {/* Last Contact */}
                  <td className="px-4 py-3 text-sm text-ink-3">
                    {customer.lastContactDate
                      ? formatDate(customer.lastContactDate)
                      : '—'}
                  </td>

                  {/* Actions — stop row-click (which opens the detail modal) */}
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setMessageTarget(customer)}
                        disabled={!customer.phoneNumber}
                        title={
                          customer.phoneNumber
                            ? `Message ${displayName(customer)}`
                            : 'No phone number on file'
                        }
                        className="rounded-xl p-1.5 text-brand transition-colors hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChatBubbleLeftRightIcon className="h-4 w-4" />
                      </button>
                      {customer.phoneNumber ? (
                        <CallCustomerButton
                          compact
                          customerPhone={customer.phoneNumber}
                          customerName={displayName(customer)}
                        />
                      ) : (
                        <button
                          disabled
                          title="No phone number on file"
                          className="cursor-not-allowed rounded-xl p-1.5 text-brand opacity-40"
                        >
                          <PhoneIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedId && (
        <CustomerDetailModal
          customerId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}

      {/* Outbound message compose — prefilled for the chosen customer. Keyed by
          id so reopening for a different customer resets the fields. */}
      {messageTarget && (
        <NewConversationModal
          key={messageTarget.id}
          isOpen={!!messageTarget}
          onClose={() => setMessageTarget(null)}
          initialPhone={messageTarget.phoneNumber}
          initialName={displayName(messageTarget)}
          onSent={() => {
            setMessageTarget(null);
            navigate('/dashboard');
          }}
        />
      )}
    </div>
  );
}
